/**
 * 邮百工GUI统一渲染逻辑 - 内容处理工具
 * 负责检测和预处理各种内容类型，为渲染做准备
 */

/**
 * 检测内容类型，识别混合内容
 * @param {string} content - 要检测类型的内容
 * @returns {Object} - 返回内容类型信息，包含primaryType和types数组
 */
export const detectContentType = (content) => {
  if (!content || typeof content !== 'string') {
    return { primaryType: 'text', types: ['text'] };
  }

  // 初始化结果对象
  const result = {
    primaryType: 'markdown', // 默认类型
    types: []
  };

  // 增强的Mermaid图表检测
  const mermaidPatterns = [
    /```mermaid/i,
    /graph\s+[A-Z]{1,2}[\s;]/i,
    /flowchart\s+[A-Z]{1,2}[\s;]/i,
    /sequenceDiagram/i,
    /classDiagram/i,
    /erDiagram/i,
    /gantt/i,
    /pie\s*title/i,
    /stateDiagram/i,
    /quadrantChart/i,
    /mindmap/i,
    /requirementDiagram/i,
    /journey/i,
    /timeline/i,
    /gitGraph/i,
    /sankey/i,
    /C4Context|C4Container|C4Component|C4Dynamic|C4Deployment/i,
    // 检测常见的节点连接模式
    /[A-Za-z0-9_]+\s*-->\s*[A-Za-z0-9_]+/,
    /[A-Za-z0-9_]+\s*---\s*[A-Za-z0-9_]+/,
    // 检测带标签的节点
    /[A-Za-z0-9_]+\[.+\]/,
    // 检测Unicode箭头（大模型常用）
    /[→←↔⇒⇐⇔]/,
    // 检测中文标签模式
    /[\u4e00-\u9fa5]+.*-->/
  ];
  
  let mermaidConfidence = 0;
  mermaidPatterns.forEach(pattern => {
    if (pattern.test(content)) {
      mermaidConfidence++;
    }
  });
  
  // 如果置信度大于1，认为包含Mermaid内容
  if (mermaidConfidence > 1) {
    result.types.push('mermaid');
  }

  // 检测Markdown表格
  const tableRegex = /\|(.+)\|\n\|(\s*:?-+:?\s*\|)+/;
  const hasTable = tableRegex.test(content);
  if (hasTable) {
    result.types.push('markdown-table');
  }

  // 检测代码块
  const codeBlockRegex = /```[\w-]*\n[\s\S]*?\n```/g;
  const hasCodeBlock = codeBlockRegex.test(content);
  if (hasCodeBlock) {
    result.types.push('code-block');

  }

  // 如果没有检测到特殊类型，则为普通markdown
  if (result.types.length === 0) {
    result.types.push('markdown');
  } else if (!result.types.includes('markdown')) {
    // 如果有特殊类型但不包含markdown，添加markdown作为基础类型
    result.types.push('markdown');
  }

  // 确定主要类型
  if (result.types.includes('mermaid') && (content.trim().startsWith('```mermaid') || content.trim().match(/^(graph|sequenceDiagram|classDiagram|erDiagram|flowchart|gantt|pie|stateDiagram)/))) {
    // 如果内容以mermaid开始，将mermaid设为主要类型
    result.primaryType = 'mermaid';
  } else if (result.types.includes('markdown-table') && content.trim().startsWith('|')) {
    // 如果内容以表格开始，将表格设为主要类型
    result.primaryType = 'markdown-table';
  } else if (result.types.includes('code-block') && content.trim().startsWith('```')) {
    // 如果内容以代码块开始，将代码块设为主要类型
    result.primaryType = 'code-block';
  }

  return result;
};

/**
 * 提取内容中的所有代码块
 * @param {string} content - 要处理的内容
 * @returns {Array} - 返回提取的代码块数组，每个元素包含language和code
 */
export const extractCodeBlocks = (content) => {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const codeBlockRegex = /```([\w-]*)\n([\s\S]*?)\n```/g;
  const codeBlocks = [];
  let match;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    codeBlocks.push({
      language: match[1] || 'text',
      code: match[2]
    });
  }

  return codeBlocks;
};



/**
 * 修复大模型生成内容中的常见问题
 * @param {string} text - 待修复的文本
 * @returns {string} - 修复后的文本
 */
const fixLLMGeneratedContent = (text) => {
  if (!text) return '';
  
  let fixed = text;
  
  // 0. 优先保护所有 Mermaid 箭头，避免后续处理意外破坏
  // 使用占位符保护（处理完成后恢复）
  const arrowPlaceholders = {
    '-->>': '__ARROW_ASYNC_RETURN__',
    '->>': '__ARROW_SYNC_RETURN__',
    '-->': '__ARROW_SOLID__',
    '---': '__ARROW_DOTTED__',
    '==>': '__ARROW_THICK__',
    '<-->': '__ARROW_BIDIRECTIONAL__',
    '<->': '__ARROW_BIDIRECTIONAL_THIN__',
    '-.->': '__ARROW_DOTTED_THIN__',
    '--x': '__ARROW_CROSS__',
    '-x': '__ARROW_SYNC_CROSS__',
  };
  
  // 按长度降序排列，确保长箭头优先匹配
  const sortedArrows = Object.keys(arrowPlaceholders).sort((a, b) => b.length - a.length);
  for (const arrow of sortedArrows) {
    fixed = fixed.split(arrow).join(arrowPlaceholders[arrow]);
  }
  
  // 1. 处理Unicode箭头符号
  fixed = fixed
    .replace(/→/g, '-->')
    .replace(/←/g, '<--')
    .replace(/↔/g, '<-->')
    .replace(/⇒/g, '==>')
    .replace(/⇐/g, '<==')
    .replace(/⇔/g, '<==>')
    .replace(/—/g, '--')
    .replace(/–/g, '--');
  
  // 2. 处理全角字符转半角
  fixed = fixed
    .replace(/（/g, '(')
    .replace(/）/g, ')')
    .replace(/［/g, '[')
    .replace(/］/g, ']')
    .replace(/｛/g, '{')
    .replace(/｝/g, '}')
    .replace(/：/g, ':')
    .replace(/；/g, ';')
    .replace(/，/g, ',')
    .replace(/。/g, '.')
    .replace(/！/g, '!')
    .replace(/？/g, '?');
  
  // 3. 统一引号
  fixed = fixed
    .replace(/[""]/g, '"')
    .replace(/['']/g, "'");
  
  // 3.1 处理列表语法冲突: [1. Text] -> [1.Text]
  // 避免 Mermaid 将 "数字. 空格" 识别为 Markdown 有序列表
  fixed = fixed.replace(/\[(\d+)\.\s+([^\]]+)\]/g, '[$1.$2]');

  // 3.2 处理非圆形节点中的 <br/>（仅圆形节点支持换行）
  // 方括号/单圆括号节点中的 <br/> 替换为空格，避免解析失败
  fixed = fixed
    .replace(/\[([^\]]*?)<br\s*\/?>\s*([^\]]*?)\]/gi, '[$1 $2]')
    .replace(/\((?!\()([^()]*?)<br\s*\/?>\s*([^()]*)\)(?!\))/gi, '($1 $2)');

  // 3.2.1 处理圆形/圆角/菱形节点中的列表语法冲突: (1. Text) / {1. Text} / ((1. Text))
  fixed = fixed
    .replace(/\(\(([^)]*?\d+)\.\s+([^)]*?)\)\)/g, '(($1.$2))')
    .replace(/\(([^()]*?\d+)\.\s+([^()]*)\)/g, '($1.$2)')
    .replace(/\{([^{}]*?\d+)\.\s+([^{}]*)\}/g, '{$1.$2}');

  // 3.2.2 处理连线标签中的列表语法冲突: -->|1. Text| -> -->|1.Text|
  fixed = fixed.replace(/\|(\s*\d+)\.\s+([^|]+)\|/g, '|$1.$2|');

  // 3.3 处理节点标签中的特殊字符（仅限方括号标签，避免影响形状语法）
  // 将可能导致解析问题的引号/括号替换为中文全角符号
  fixed = fixed.replace(/\[([^"\]]*?)\]/g, (match, content) => {
    // 若已经有引号包围，保持原样
    if (content.startsWith('"') && content.endsWith('"')) {
      return match;
    }
    const safe = content
      .replace(/"/g, '『').replace(/'/g, '』')
      .replace(/\(/g, '「').replace(/\)/g, '」');
    return `[${safe}]`;
  });

  // 4. 处理中文标签 - 为未加引号的中文文本添加引号
  // 匹配模式：节点定义中的中文文本
  fixed = fixed.replace(/([A-Za-z0-9_]+)(\[)([^"\]]*[\u4e00-\u9fa5]+[^"\]]*)(\])/g, (match, nodeId, leftBracket, content, rightBracket) => {
    // 如果内容还没有被引号包围，则添加引号
    if (!content.startsWith('"') || !content.endsWith('"')) {
      return `${nodeId}${leftBracket}"${content.replace(/"/g, '\\"')}"${rightBracket}`;
    }
    return match;
  });
  
  // 4.5. 处理包含特殊字符的英文标签（如缩写、括号等）
  fixed = fixed.replace(/([A-Za-z0-9_]+)(\[)([^"\]]*(?:e\.g\.|i\.e\.|etc\.|[(),.])[^"\]]*)(\])/g, (match, nodeId, leftBracket, content, rightBracket) => {
    // 如果内容包含特殊字符且未加引号，则添加引号
    if (!content.startsWith('"') || !content.endsWith('"')) {
      return `${nodeId}${leftBracket}"${content.replace(/"/g, '\\"')}"${rightBracket}`;
    }
    return match;
  });
  
  // 4.6. 处理文件路径符号（特别是根目录 / 和其他路径字符）
  fixed = fixed.replace(/([A-Za-z0-9_]+)(\[)([^"\]]*[\/\\][^"\]]*)(\])/g, (match, nodeId, leftBracket, content, rightBracket) => {
    // 如果内容包含路径字符且未加引号，则添加引号
    if (!content.startsWith('"') || !content.endsWith('"')) {
      return `${nodeId}${leftBracket}"${content.replace(/"/g, '\\"')}"${rightBracket}`;
    }
    return match;
  });
  
  // 5. 处理连接线上的中文标签（排除序列图箭头 -->> ->> 等）
  // 使用负向前瞻确保 --> 后面不是 > 字符（避免破坏 -->> 箭头）
  fixed = fixed.replace(/(-->(?!>)|---|==>(?!>)) *([^"\n|>]+[\u4e00-\u9fa5]+[^"\n|]*) *(\||$)/g, (match, arrow, label, ending) => {
    if (!label.trim().startsWith('"') || !label.trim().endsWith('"')) {
      return `${arrow}"${label.trim()}"${ending}`;
    }
    return match;
  });
  
  // 6. 处理HTML转义字符
  fixed = fixed
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // 7. 处理subgraph标识符中的空格和特殊字符
  fixed = fixed.replace(/subgraph\s+([^{}\n]+)/g, (match, label) => {
    // 移除标识符中的空格、括号等特殊字符，只保留中英文和数字
    const cleanLabel = label.trim()
      .replace(/[\s\(\)（）\[\]【】\{\}]/g, '_') // 将空格和各种括号替换为下划线
      .replace(/[^\w\u4e00-\u9fa5_]/g, '') // 只保留字母数字中文和下划线
      .replace(/_+/g, '_') // 多个下划线合并为一个
      .replace(/^_|_$/g, ''); // 移除开头和结尾的下划线
    
    // 如果处理后的标识符为空，使用默认名称
    const finalLabel = cleanLabel || 'group';
    return `subgraph ${finalLabel}`;
  });
  
  // 8. 处理非标准的note语法，转换为注释
  fixed = fixed.replace(/^\s*note\s+on\s+([^:]+):\s*(.+)$/gm, (match, nodeId, noteText) => {
    // 将 note 语法转换为标准的注释格式
    return `%% ${nodeId.trim()}: ${noteText.trim()}`;
  });
  
  // 8.1. 处理Gantt图中的非标准语法
  // 检测是否为Gantt图
  const isGantt = /^\s*gantt/i.test(fixed);
  if (isGantt) {
    // 移除不支持的criticalPath语法，转换为注释
    fixed = fixed.replace(/^\s*criticalPath\s+(.+)$/gm, (match, pathContent) => {
      return `%% Critical Path: ${pathContent.trim()}`;
    });
    
    // 处理其他可能的非标准Gantt语法
    fixed = fixed.replace(/^\s*excludes\s+(.+)$/gm, (match, excludeContent) => {
      return `%% Excludes: ${excludeContent.trim()}`;
    });
    
    // 处理Gantt图中的中文标题 - 确保用引号包围
    fixed = fixed.replace(/^\s*title\s+(.+)$/gm, (match, titleContent) => {
      const cleanTitle = titleContent.trim();
      // 如果标题包含中文且未用引号包围，则添加引号
      if (/[\u4e00-\u9fa5]/.test(cleanTitle) && !cleanTitle.startsWith('"')) {
        return `title "${cleanTitle}"`;
      }
      return match;
    });
    
    // 处理section标题 - 确保用引号包围
    fixed = fixed.replace(/^\s*section\s+(.+)$/gm, (match, sectionContent) => {
      const cleanSection = sectionContent.trim();
      // 如果section包含中文且未用引号包围，则添加引号
      if (/[\u4e00-\u9fa5]/.test(cleanSection) && !cleanSection.startsWith('"')) {
        return `section "${cleanSection}"`;
      }
      return match;
    });
    
    // 处理任务名称 - 确保中文任务名用引号包围
    fixed = fixed.replace(/^\s*([^:]+)\s*:\s*(.+)$/gm, (match, taskName, taskData) => {
      const cleanTaskName = taskName.trim();
      // 检查是否为有效的任务行（不是title、section、dateFormat等关键字）
      if (!/^(gantt|title|section|dateFormat|axisFormat|excludes|includes)(\s|$)/i.test(cleanTaskName)) {
        // 如果任务名包含中文且未用引号包围，则添加引号
        if (/[\u4e00-\u9fa5]/.test(cleanTaskName) && !cleanTaskName.startsWith('"') && !cleanTaskName.endsWith('"')) {
          return `"${cleanTaskName}" : ${taskData}`;
        }
      }
      return match;
    });
  }
  
  // 9. 规范化空格和换行（需要特别处理mindmap、序列图等类型）
  const isMindmap = /^\s*mindmap/i.test(fixed) || fixed.includes('mindmap');
  const isSequenceDiagram = /^\s*sequenceDiagram/i.test(fixed) || fixed.includes('sequenceDiagram');
  const isIndentSensitive = isMindmap || isSequenceDiagram || /^\s*(timeline|journey|gantt)/i.test(fixed);
  
  if (isIndentSensitive) {
    // 对于依赖缩进的图表类型，完全保持原有的空格和换行结构
    // 只处理箭头周围的空格（mindmap通常不用箭头，所以这部分影响很小）
    fixed = fixed
      .replace(/\n\s*\n\s*\n/g, '\n\n'); // 只合并多个连续空行
  } else {
    // 对于其他图表类型，进行正常的空格规范化
    fixed = fixed
      // 使用临时占位符保护复合箭头（序列图箭头优先处理，避免被拆分）
      .replace(/\s*<-->\s*/g, '__BIDIRECTIONAL_ARROW__')
      .replace(/\s*<==>\s*/g, '__BIDIRECTIONAL_DOUBLE_ARROW__')
      .replace(/\s*-->>\s*/g, '__ASYNC_RETURN_ARROW__')     // 序列图: 异步返回
      .replace(/\s*-->>>\s*/g, '__ASYNC_RETURN_ARROW3__')   // 保护三箭头（如果存在）
      .replace(/\s*->>>\s*/g, '__SYNC_RETURN_ARROW3__')     // 保护三箭头
      .replace(/\s*->>\s*/g, '__SYNC_RETURN_ARROW__')       // 序列图: 同步返回
      .replace(/\s*--x\s*/gi, '__LOST_MESSAGE_ARROW__')     // 序列图: 丢失消息
      .replace(/\s*-x\s*/gi, '__SYNC_LOST_ARROW__')         // 序列图: 同步丢失
      // 处理单向箭头（此时复合箭头已被保护）
      .replace(/\s*-->\s*/g, ' --> ')
      .replace(/\s*---\s*/g, ' --- ')
      .replace(/\s*==>\s*/g, ' ==> ')
      .replace(/\s*<--\s*/g, ' <-- ')
      .replace(/\s*<==\s*/g, ' <== ')
      .replace(/\s*->(?!>)\s*/g, ' -> ')                    // 序列图: 同步调用（避免匹配 ->>）
      // 处理箭头标签：确保 -->|label| 格式正确
      .replace(/(-->|---|==>|<--|<==)\s*\|\s*([^|]+?)\s*\|/g, '$1|$2|')
      // 然后处理表格和其他管道符（但避免影响已处理的箭头标签）
      .replace(/(\s)\|\s+(?![^|]*-->)/g, '$1| ') // 表格开头的管道符
      .replace(/\s+\|(\s)/g, ' |$1') // 表格结尾的管道符
      .replace(/[ \t]+/g, ' ') // 多个空格合并为一个
      .replace(/\n\s*\n\s*\n/g, '\n\n') // 多个空行合并为两个
      // 最后恢复所有被保护的箭头
      .replace(/__BIDIRECTIONAL_ARROW__/g, ' <--> ')
      .replace(/__BIDIRECTIONAL_DOUBLE_ARROW__/g, ' <==> ')
      .replace(/__ASYNC_RETURN_ARROW3__/g, ' -->>> ')
      .replace(/__ASYNC_RETURN_ARROW__/g, ' -->> ')
      .replace(/__SYNC_RETURN_ARROW3__/g, ' ->>> ')
      .replace(/__SYNC_RETURN_ARROW__/g, ' ->> ')
      .replace(/__LOST_MESSAGE_ARROW__/g, ' --x ')
      .replace(/__SYNC_LOST_ARROW__/g, ' -x ');
  }
  
  // 最后恢复函数开头保护的箭头（按原样恢复，不加空格）
  fixed = fixed
    .replace(/__ARROW_ASYNC_RETURN__/g, '-->>')
    .replace(/__ARROW_SYNC_RETURN__/g, '->>')
    .replace(/__ARROW_SOLID__/g, '-->')
    .replace(/__ARROW_DOTTED__/g, '---')
    .replace(/__ARROW_THICK__/g, '==>')
    .replace(/__ARROW_BIDIRECTIONAL__/g, '<-->')
    .replace(/__ARROW_BIDIRECTIONAL_THIN__/g, '<->')
    .replace(/__ARROW_DOTTED_THIN__/g, '-.->')
    .replace(/__ARROW_CROSS__/g, '--x')
    .replace(/__ARROW_SYNC_CROSS__/g, '-x');
  
  return fixed.trim();
};

/**
 * 移除 Mermaid init 中的主题设置，避免覆盖全局主题
 * 仅删除 theme / themeVariables 字段，其它 init 配置保持不变
 */
const stripMermaidInitTheme = (chart) => {
  if (!chart || typeof chart !== 'string') return chart;

  return chart.replace(/%%\{init:\s*\{[\s\S]*?\}\}%%/g, (block) => {
    let cleaned = block;
    // 移除 themeVariables 块
    cleaned = cleaned.replace(/themeVariables\s*:\s*\{[\s\S]*?\}\s*,?/g, '');
    // 移除 theme 字段
    cleaned = cleaned.replace(/theme\s*:\s*['"][^'"]+['"]\s*,?/g, '');
    // 清理多余逗号
    cleaned = cleaned
      .replace(/,\s*}/g, '}')
      .replace(/\{\s*,/g, '{');

    // 如果 init 为空，直接移除整个块
    if (/%%\{init:\s*\{\s*\}\s*\}%%/.test(cleaned)) {
      return '';
    }
    return cleaned;
  });
};

/**
 * 修复大模型生成的异常Markdown表格内容
 * 处理连续的表格分隔行和大量横线的情况
 * @param {string} content - 待修复的内容
 * @returns {string} - 修复后的内容
 */
const fixAbnormalMarkdownTable = (content) => {
  if (!content || typeof content !== 'string') return content;
  
  let fixed = content;
  
  // 1. 检测并修复连续的表格分隔行（换行情况）
  // 匹配连续3行以上的 |---|---|...| 格式
  const tableLinePattern = /(\|[\s-|]+\|\s*\n){3,}/g;
  
  if (tableLinePattern.test(fixed)) {
    console.warn('⚠️ 检测到异常的表格分隔行（连续3行以上），已自动修复');
    
    // 将连续的表格分隔线减少为最多2行（表头+分隔线）
    fixed = fixed.replace(
      /(\|[\s-|]+\|\s*\n){3,}/g,
      (match) => {
        const lines = match.trim().split('\n');
        // 只保留前2行
        return lines.slice(0, 2).join('\n') + '\n';
      }
    );
  }
  
  // 2. 检测并修复连续的纯横线（不换行情况）
  // 匹配连续20个以上的横线
  const dashPattern = /-{20,}/g;
  
  if (dashPattern.test(fixed)) {
    console.warn('⚠️ 检测到异常的连续横线（单行20+个），已自动修复');
    
    // 将连续的横线限制为最多3个（Markdown水平线）
    fixed = fixed.replace(/-{20,}/g, '---');
  }
  
  return fixed;
};

/**
 * 验证Mermaid语法
 * @param {string} chart - Mermaid图表内容
 * @returns {Object} - 验证结果 {isValid: boolean, errors: Array, warnings: Array}
 */
export const validateMermaidSyntax = (chart) => {
  const result = {
    isValid: true,
    errors: [],
    warnings: [],
    suggestions: []
  };
  
  if (!chart || !chart.trim()) {
    result.isValid = false;
    result.errors.push('图表内容为空');
    return result;
  }
  
  const lines = chart.split('\n').map(line => line.trim()).filter(line => line);
  
  // 检查图表类型
  const firstLine = lines[0];
  const supportedTypes = [
    'graph', 'flowchart', 'sequenceDiagram', 'classDiagram', 'erDiagram',
    'gantt', 'pie', 'stateDiagram', 'quadrantChart', 'mindmap',
    'requirementDiagram', 'journey', 'timeline', 'gitGraph', 'sankey',
    'C4Context', 'C4Container', 'C4Component', 'C4Dynamic', 'C4Deployment'
  ];
  
  const hasValidType = supportedTypes.some(type => firstLine.startsWith(type));
  if (!hasValidType) {
    result.warnings.push('未检测到明确的图表类型，将尝试自动添加');
  }

  // 检查 graph/flowchart 是否显式设置方向（TB/LR等）
  if (/^(graph|flowchart)\b/i.test(firstLine) && !/^(graph|flowchart)\s+(TB|BT|LR|RL|TD)\b/i.test(firstLine)) {
    result.warnings.push('建议在 graph/flowchart 首行设置方向（TB/LR等）');
  }

  // 仅对 graph/flowchart 类型进行节点引用检查（避免误伤其他类型）
  const shouldValidateNodeRefs = /^(graph|flowchart)\b/i.test(firstLine);
  const definedNodeIds = new Set();
  const definedSubgraphIds = new Set();
  
  // 检查常见语法错误
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // 检查未闭合的括号
    const openBrackets = (line.match(/[\(\[\{]/g) || []).length;
    const closeBrackets = (line.match(/[\)\]\}]/g) || []).length;
    if (openBrackets !== closeBrackets) {
      result.warnings.push(`第${i + 1}行: 括号可能未正确配对`);
    }
    
    // 检查中文字符是否需要引号
    if (/[\u4e00-\u9fa5]/.test(line) && !/".*[\u4e00-\u9fa5].*"/.test(line)) {
      result.warnings.push(`第${i + 1}行: 包含中文字符，建议使用引号包围`);
    }
    
    // 检查箭头语法
    if (/[→←↔⇒⇐⇔—–]/.test(line)) {
      result.warnings.push(`第${i + 1}行: 检测到Unicode箭头字符，已自动转换为标准语法`);
    }
    
    // 检查HTML转义字符
    if (/&(gt|lt|amp|quot);/.test(line)) {
      result.warnings.push(`第${i + 1}行: 检测到HTML转义字符，已自动转换`);
    }
    
    // 检查subgraph语法
    if (line.includes('subgraph') && /subgraph\s+.*[\s\(\)（）]/.test(line)) {
      result.warnings.push(`第${i + 1}行: subgraph标识符包含空格或括号，已自动修复`);
    }

    // 收集 subgraph ID（用于引用检查）
    if (shouldValidateNodeRefs) {
      const subgraphMatch = line.match(/^\s*subgraph\s+([A-Za-z0-9_]+)/);
      if (subgraphMatch?.[1]) {
        definedSubgraphIds.add(subgraphMatch[1]);
      }
    }
    
    // 检查箭头标签语法错误：--> | label | 
    if (/(-->|---|==>|<--|<==|<-->|<==>)\s+\|\s+[^|]+\s+\|/.test(line)) {
      result.errors.push(`第${i + 1}行: 箭头标签语法错误，应该是 -->|label| 而不是 --> | label |`);
      result.suggestions.push('修正箭头标签格式：移除管道符前后的空格');
    }
    
    // 检查双向箭头语法错误：< --> 或 < ==> 
    if (/\s<\s+-->|\s<\s+==>/.test(line)) {
      result.errors.push(`第${i + 1}行: 双向箭头语法错误，应该是 <--> 或 <==> 而不是分离的格式`);
      result.suggestions.push('修正双向箭头格式：移除箭头符号中间的空格');
    }
    
    // 检查非标准的note语法
    if (/^\s*note\s+on\s+/.test(line)) {
      result.warnings.push(`第${i + 1}行: 检测到非标准note语法，已转换为注释`);
    }
    
    // 检查节点标签中的特殊字符
    if (/\[[^"\]]*(?:e\.g\.|i\.e\.|[(),.])[^"\]]*\]/.test(line) && !/\["[^"]*"\]/.test(line)) {
      result.warnings.push(`第${i + 1}行: 节点标签包含特殊字符，建议使用引号包围`);
    }
    
    // 检查节点标签中的文件路径字符
    if (/\[[^"\]]*[\/\\][^"\]]*\]/.test(line) && !/\["[^"]*"\]/.test(line)) {
      result.warnings.push(`第${i + 1}行: 节点标签包含文件路径字符，已自动添加引号`);
    }
    
    // 检查Gantt图中的非标准语法
    if (/^\s*criticalPath\s+/.test(line)) {
      result.warnings.push(`第${i + 1}行: 检测到非标准Gantt语法 criticalPath，已转换为注释`);
    }
    
    if (/^\s*excludes\s+/.test(line)) {
      result.warnings.push(`第${i + 1}行: 检测到非标准Gantt语法 excludes，已转换为注释`);
    }
    
    // 检查Gantt图中的中文文本是否需要引号
    if (/^\s*(title|section)\s+/.test(line) && /[\u4e00-\u9fa5]/.test(line) && !/["']/.test(line)) {
      result.warnings.push(`第${i + 1}行: Gantt图标题或section包含中文，已自动添加引号`);
    }
    
    // 检查Gantt任务名称是否需要引号
    if (/^\s*[^:]*[\u4e00-\u9fa5][^:]*\s*:\s*/.test(line) && !/^\s*"/.test(line)) {
      result.warnings.push(`第${i + 1}行: Gantt任务名称包含中文，已自动添加引号`);
    }

    // 仅对 graph/flowchart 进行节点定义与引用的弱校验（只给提示，不阻断渲染）
    if (shouldValidateNodeRefs) {
      // 1) 记录节点定义（常见形状）
      const nodeDefMatch = line.match(/^\s*([A-Za-z0-9_]+)\s*(\[[^\]]*\]|\([^)]+\)|\(\([^)]+\)\)|\{[^}]+\}|>[^]]+\])/);
      if (nodeDefMatch?.[1]) {
        definedNodeIds.add(nodeDefMatch[1]);
      }

      // 2) 检查样式声明语法（轻量校验）
      if (/^\s*style\s+/i.test(line) && !/^\s*style\s+[A-Za-z0-9_,\s]+\s+fill:/i.test(line)) {
        result.warnings.push(`第${i + 1}行: style 语法可能不规范，建议以 "style NodeID fill:#color" 开头`);
      }

      // 3) 检查引用的节点是否已定义（只对明显的箭头连接做提示）
      const arrowMatch = line.match(/([A-Za-z0-9_]+)\s*(-->|---|==>|<--|<==|<-->|\-\.->|<-\.\->|--o|o--|~~>|-.->)\s*([A-Za-z0-9_&\s]+)/);
      if (arrowMatch) {
        const fromId = arrowMatch[1];
        const toPart = arrowMatch[3] || '';
        const toIds = toPart.split('&').map(v => v.trim()).filter(Boolean);

        if (fromId && !definedNodeIds.has(fromId) && !definedSubgraphIds.has(fromId)) {
          result.warnings.push(`第${i + 1}行: 连接起点 "${fromId}" 未检测到定义`);
        }
        toIds.forEach((id) => {
          if (id && !definedNodeIds.has(id) && !definedSubgraphIds.has(id)) {
            result.warnings.push(`第${i + 1}行: 连接终点 "${id}" 未检测到定义`);
          }
        });
      }
    }
  }
  
  // 提供改进建议
  if (result.warnings.length > 0) {
    result.suggestions.push('已自动修复检测到的格式问题');
  }
  
  if (result.errors.length === 0 && result.warnings.length <= 2) {
    result.suggestions.push('语法检查通过，图表应该能正常渲染');
  }
  
  return result;
};

/**
 * 预处理Mermaid图表内容 - 增强版
 * @param {string} chart - 原始图表内容
 * @param {boolean} skipAutoFix - 是否跳过自动修复（AI修复后的代码应该跳过）
 * @returns {Object} - 处理结果 {content: string, validation: Object, originalContent: string}
 */
export const preprocessMermaidChart = (chart, skipAutoFix = false) => {
  if (!chart) return { content: '', validation: { isValid: false, errors: ['内容为空'] }, originalContent: '' };

  // 保存原始内容
  const originalContent = chart;
  
  // 首先处理JSON字符串中的特殊转义
  let processedChart = chart;
  
  // 处理JSON字符串中的转义字符（如果检测到是字符串形式的JSON）
  if (chart.includes('\\\\n') || chart.includes('\\\\\"') || chart.includes('\\\\u')) {
    try {
      // 尝试使用JSON.parse处理（需要确保是有效的JSON字符串）
      // 先检查是否是完整的JSON字符串
      if ((chart.startsWith('"') && chart.endsWith('"')) || 
          (chart.startsWith("'") && chart.endsWith("'"))) {
        // 解析JSON字符串
        const jsonString = JSON.parse(chart);
        processedChart = jsonString;
      } else {
        // 手动替换常见的转义序列
        processedChart = chart
          .replace(/\\\\n/g, '\n')
          .replace(/\\\\\"/g, '"')
          .replace(/\\\\u003e/g, '>')
          .replace(/\\\\u003c/g, '<')
          .replace(/\\\\/g, '\\');
      }
    } catch (e) {
      console.error('处理Mermaid转义字符时出错:', e);
      // 发生错误时，尝试基本替换
      processedChart = chart
        .replace(/\\\\n/g, '\n')
        .replace(/\\\\\"/g, '"')
        .replace(/\\\\u003e/g, '>')
        .replace(/\\\\u003c/g, '<')
        .replace(/\\\\/g, '\\');
    }
  }

  // 移除多余的反斜杠转义
  processedChart = processedChart.replace(/\\([[\]{}()\\])/g, '$1');

  // 提取mermaid代码块中的内容
  if (processedChart.includes('```mermaid')) {
    const match = processedChart.match(/^\s*```mermaid\n([\s\S]*?)\n\s*```\s*$/m);
    if (match && match[1]) {
      processedChart = match[1];
    }
  }

  // 应用大模型内容修复（除非跳过自动修复）
  if (!skipAutoFix) {
    processedChart = fixLLMGeneratedContent(processedChart);
  }

  // 移除 Mermaid init 里显式的主题设置，避免覆盖全局深色主题
  processedChart = stripMermaidInitTheme(processedChart);

  // 移除自动添加前缀的逻辑，保持内容原样
  // 这样可以避免对已有正确前缀的内容进行重复处理
  // 让大模型生成的内容和用户输入的内容保持原始状态

  // 验证语法
  const validation = validateMermaidSyntax(processedChart);

  return {
    content: processedChart,
    validation,
    originalContent
  };
};

/**
 * 预处理Markdown表格内容
 * @param {string} tableContent - 原始表格内容
 * @returns {string} - 处理后的表格内容
 */
export const preprocessMarkdownTable = (tableContent) => {
  if (!tableContent) return '';

  // 检查表格是否有分隔线，如果没有，添加分隔线
  const lines = tableContent.split('\n');
  if (lines.length >= 2) {
    const headerLine = lines[0];
    // 计算表头有多少列
    const columnCount = headerLine.split('|').filter(cell => cell.trim() !== '').length;
    
    // 检查是否已有分隔线
    const secondLine = lines[1];
    const isSeparator = /^\|(\s*:?-+:?\s*\|)+$/.test(secondLine);
    
    // 如果没有分隔线，插入一个
    if (!isSeparator) {
      const separator = '|' + Array(columnCount).fill('---').join('|') + '|';
      lines.splice(1, 0, separator);
      return lines.join('\n');
    }
  }

  return tableContent;
};

/**
 * 统一处理各种内容类型
 * @param {string} content - 要处理的内容
 * @returns {Object} - 返回处理后的内容和类型信息
 */
export const processContent = (content) => {
  if (!content) {
    return { content: '', primaryType: 'text', types: ['text'] };
  }

  // 🔧 修复大模型生成的异常表格内容
  let processedContent = fixAbnormalMarkdownTable(content);

  // 检测内容类型
  const { primaryType, types } = detectContentType(processedContent);

  // 根据主要类型预处理内容
  if (primaryType === 'mermaid') {
    processedContent = preprocessMermaidChart(content);
  } else if (primaryType === 'markdown-table') {
    processedContent = preprocessMarkdownTable(content);
  }

  // 返回处理结果
  return {
    content: processedContent,
    primaryType,
    types
  };
}; 